function mountBaitBoss(root) {
  const signal = (root.bbController = new AbortController()).signal;
  const listen = (el, type, fn, options = {}) => el.addEventListener(type, fn, { ...options, signal });
  let observer;
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const story = root.querySelector('.scroll-story');
  const stage = root.querySelector('.stage');
  const images = [...root.querySelectorAll('.hero-image')];
  const beats = [...root.querySelectorAll('.beat')];
  const heroProgress = root.querySelector('.progress b');
  const line = root.querySelector('.line-trace span');
  const reel = root.querySelector('.reel-focus');
  const reelHandle = root.querySelector('.reel-focus i');
  const nav = document.querySelector('.nav');
  const pageProgress = root.querySelector('.page-progress');
  const pageBar = pageProgress?.querySelector('span');
  const pageNumber = pageProgress?.querySelector('em');
  const soundToggle = root.querySelector('.sound-toggle');
  const motionStrip = root.querySelector('.motion-strip div');
  const parallaxItems = [...root.querySelectorAll('.product-photo img, .reel-photo img, .tackle-visual img, .detail-gallery img, .complete-image img')];
  const clamp = (n, min = 0, max = 1) => Math.min(max, Math.max(min, n));
  let soundEnabled = false;
  let strikePlayed = false;

  const revealGroups = [
    ['.section-heading', 'reveal-up'],
    ['.spec-grid article', 'reveal-card'],
    ['.shop-heading > *', 'reveal-up'],
    ['.product-option:first-child', 'reveal-left'],
    ['.product-option:last-child', 'reveal-right'],
    ['.reel-copy', 'reveal-left'],
    ['.reel-photo', 'reveal-right'],
    ['.reel-section dl > div', 'reveal-up'],
    ['.tackle-visual', 'reveal-left'],
    ['.tackle-copy > .eyebrow, .tackle-copy > h2, .tackle-lead', 'reveal-up'],
    ['.tackle-list article', 'reveal-right'],
    ['.detail-copy', 'reveal-left'],
    ['.detail-gallery figure', 'reveal-image'],
    ['.complete-image', 'reveal-left'],
    ['.complete-copy', 'reveal-right'],
    ['.product-guide', 'reveal-up'],
    ['.story-section > *', 'reveal-up']
  ];

  revealGroups.forEach(([selector, className]) => {
    root.querySelectorAll(selector).forEach((element, index) => {
      element.classList.add('scroll-reveal', className);
      element.style.setProperty('--delay', `${Math.min(index, 5) * 85}ms`);
    });
  });

  if (!reduceMotion && 'IntersectionObserver' in window) {
    observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('in-view');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.16, rootMargin: '0px 0px -7% 0px' });
    root.querySelectorAll('.scroll-reveal').forEach((element) => observer.observe(element));
  } else {
    root.querySelectorAll('.scroll-reveal').forEach((element) => element.classList.add('in-view'));
  }

  const reelAudio = new Audio(root.dataset.audio);
  reelAudio.preload = 'auto';
  reelAudio.volume = .85;

  function updateSoundButton() {
    soundToggle?.setAttribute('aria-pressed', String(soundEnabled));
    soundToggle?.setAttribute('aria-label', soundEnabled ? 'Turn fishing sound off' : 'Turn fishing sound on');
    if (soundToggle) soundToggle.querySelector('span').textContent = soundEnabled ? 'Sound on' : 'Sound off';
  }

  function playReelDrag() {
    if (!soundEnabled || document.hidden) return;
    reelAudio.currentTime = 0;
    reelAudio.play().catch(() => {
      soundEnabled = false;
      updateSoundButton();
    });
  }

  listen(soundToggle, 'click', () => {
    soundEnabled = !soundEnabled;
    updateSoundButton();
    if (soundEnabled) {
      const storyRect = story.getBoundingClientRect();
      const storyDistance = Math.max(1, story.offsetHeight - innerHeight);
      strikePlayed = clamp(-storyRect.top / storyDistance) >= .43;
      // A real-sound preview also unlocks playback during subsequent scrolling.
      playReelDrag();
    } else {
      reelAudio.pause();
      reelAudio.currentTime = 0;
    }
  });

  listen(document, 'visibilitychange', () => {
    if (document.hidden) {
      reelAudio.pause();
      reelAudio.currentTime = 0;
    }
  });

  let targetY = scrollY;
  let smoothY = scrollY;
  let raf = 0;

  function renderHero() {
    if (reduceMotion) return;
    const rect = story.getBoundingClientRect();
    const distance = Math.max(1, story.offsetHeight - innerHeight);
    const p = clamp(-rect.top / distance);
    const exact = p * images.length;
    const index = Math.min(images.length - 1, Math.floor(exact));
    const local = index === images.length - 1 ? clamp(exact - index) : exact - index;
    const nextMix = index < images.length - 1 ? clamp((local - .54) / .46) : 0;
    const mobile = innerWidth <= 800;

    stage.style.setProperty('--story-p', p.toFixed(4));
    stage.style.setProperty('--local-p', local.toFixed(4));

    images.forEach((image, i) => {
      const isCurrent = i === index;
      const isNext = i === index + 1;
      const opacity = isCurrent ? 1 - nextMix : isNext ? nextMix : 0;
      const currentX = -local * 3.5;
      const incomingX = (1 - nextMix) * 8;
      image.classList.toggle('active', opacity > .02);
      image.style.opacity = opacity.toFixed(3);
      image.style.zIndex = isNext ? '2' : isCurrent ? '1' : '0';
      image.style.transform = `translate3d(${isNext ? incomingX : currentX}%, ${isCurrent ? local * 1.2 : 0}%, 0) scale(${isNext ? 1.08 - nextMix * .05 : 1.02 + local * .055})`;
      image.style.clipPath = `inset(${isNext ? (1 - nextMix) * 9 : 0}% ${isNext ? (1 - nextMix) * 3 : 0}% ${isNext ? (1 - nextMix) * 9 : 0}% 0 round ${isNext ? 18 : 0}px)`;
    });

    beats.forEach((beat, i) => {
      const isCurrent = i === index;
      const isNext = i === index + 1;
      const opacity = isCurrent ? 1 - nextMix : isNext ? nextMix : 0;
      const y = isCurrent ? -local * 34 : (1 - nextMix) * 42;
      beat.classList.toggle('active', opacity > .04);
      beat.inert = opacity <= .04;
      beat.setAttribute('aria-hidden', String(opacity <= .04));
      beat.style.opacity = opacity.toFixed(3);
      beat.style.filter = `blur(${(1 - opacity) * 11}px)`;
      beat.style.transform = mobile
        ? `translate3d(0, ${y * .55}px, 0)`
        : `translate3d(0, calc(-50% + ${y}px), 0)`;
    });

    heroProgress.style.width = `${p * 120}px`;
    line.style.width = `${clamp((p - .31) / .43) * 100}%`;
    line.style.transform = `translateX(${(1 - clamp((p - .31) / .43)) * 18}%)`;
    reel.classList.toggle('on', p > .12 && p < .44);
    if (reelHandle) reelHandle.style.transform = `rotate(${p * 2160}deg)`;
    if (soundEnabled && p >= .43 && !strikePlayed) {
      strikePlayed = true;
      playReelDrag();
    }
    if (p < .28) strikePlayed = false;
  }

  function renderPageMotion() {
    if (reduceMotion) return;
    const maxScroll = Math.max(1, document.documentElement.scrollHeight - innerHeight);
    const pageP = clamp(smoothY / maxScroll);
    if (pageBar) pageBar.style.transform = `scaleY(${pageP})`;
    if (pageNumber) pageNumber.textContent = String(Math.round(pageP * 100)).padStart(2, '0');
    if (motionStrip) motionStrip.style.transform = `translate3d(${-12 - pageP * 25}%, 0, 0)`;
    nav.classList.toggle('scrolled', smoothY > 40);

    parallaxItems.forEach((item) => {
      const rect = item.parentElement.getBoundingClientRect();
      const center = rect.top + rect.height / 2 - innerHeight / 2;
      const amount = clamp(center / innerHeight, -1, 1);
      item.style.setProperty('--py', `${amount * -26}px`);
      item.style.setProperty('--ps', `${1.035 + Math.abs(amount) * .025}`);
    });

    const reelNumber = root.querySelector('.reel-number');
    if (reelNumber) {
      const section = reelNumber.closest('.reel-section').getBoundingClientRect();
      const sectionP = clamp((innerHeight - section.top) / (innerHeight + section.height));
      reelNumber.style.transform = `translate3d(${(sectionP - .5) * 9}vw, -50%, 0)`;
    }
  }

  function loop() {
    smoothY += (targetY - smoothY) * .115;
    renderHero();
    renderPageMotion();
    if (Math.abs(targetY - smoothY) > .1) {
      raf = requestAnimationFrame(loop);
    } else {
      smoothY = targetY;
      raf = 0;
    }
  }

  function requestRender() {
    targetY = scrollY;
    if (!raf) raf = requestAnimationFrame(loop);
  }

  listen(window, 'scroll', requestRender, { passive: true });
  listen(window, 'resize', requestRender, { passive: true });
  listen(window, 'load', () => {
    root.classList.add('loaded');
    requestRender();
  }, { once: true });
  root.classList.add('motion-ready');
  requestRender();
  root.bbCleanup = () => { root.bbController.abort(); observer?.disconnect(); cancelAnimationFrame(raf); reelAudio.pause(); };
}

function startBaitBoss() {
  const root = document.getElementById('bb-story-root');
  if (!root) return;
  root.bbCleanup?.();
  mountBaitBoss(root);
}
startBaitBoss();
document.addEventListener('shopify:section:load', startBaitBoss);
document.addEventListener('shopify:section:unload', event => event.target.querySelector('#bb-story-root')?.bbCleanup?.());
